// Unit of Measure (UOM) conversion utilities

export interface UOMConversion {
  uom: string;
  qty: number;
  label: string;
}

export interface UOMConversions {
  base: string;
  conversions: UOMConversion[];
}

// Predefined UOM conversions
export const PREDEFINED_UOM_CONVERSIONS: Record<string, UOMConversions> = {
  // ATK
  pcs: {
    base: 'pcs',
    conversions: [
      { uom: 'box', qty: 12, label: 'Box (12 pcs)' },
      { uom: 'lusin', qty: 12, label: 'Lusin (12 pcs)' },
      { uom: 'kodi', qty: 20, label: 'Kodi (20 pcs)' },
      { uom: 'gross', qty: 144, label: 'Gross (144 pcs)' },
    ],
  },
  lembar: {
    base: 'lembar',
    conversions: [
      { uom: 'rim', qty: 500, label: 'Rim (500 lembar)' },
      { uom: 'pak', qty: 100, label: 'Pak (100 lembar)' },
    ],
  },
  // IT
  unit: {
    base: 'unit',
    conversions: [
      { uom: 'box', qty: 1, label: 'Box (1 unit)' },
    ],
  },
  // General - berat
  gram: {
    base: 'gram',
    conversions: [
      { uom: 'kg', qty: 1000, label: 'Kilogram (1000 gram)' },
      { uom: 'roll', qty: 1000, label: 'Roll (1000 gram)' },
    ],
  },
  ml: {
    base: 'ml',
    conversions: [
      { uom: 'liter', qty: 1000, label: 'Liter (1000 ml)' },
      { uom: 'gallon', qty: 5000, label: 'Gallon (5000 ml)' },
    ],
  },
  // Lainnya
  roll: {
    base: 'roll',
    conversions: [
      { uom: 'box', qty: 10, label: 'Box (10 roll)' },
    ],
  },
};

// Common UOM options
export const COMMON_UOMS = [
  { value: 'pcs', label: 'Pcs' },
  { value: 'unit', label: 'Unit' },
  { value: 'box', label: 'Box' },
  { value: 'pack', label: 'Pack' },
  { value: 'rim', label: 'Rim' },
  { value: 'lembar', label: 'Lembar' },
  { value: 'roll', label: 'Roll' },
  { value: 'liter', label: 'Liter' },
  { value: 'ml', label: 'ML' },
  { value: 'gram', label: 'Gram' },
  { value: 'kg', label: 'Kilogram' },
  { value: 'set', label: 'Set' },
  { value: 'pair', label: 'Pair' },
  { value: 'lusin', label: 'Lusin' },
  { value: 'kodi', label: 'Kodi' },
];

// Convert quantity from one UOM to another
export function convertUOM(
  quantity: number,
  fromUOM: string,
  toUOM: string,
  conversions?: UOMConversions
): number {
  if (fromUOM === toUOM) return quantity;
  
  const conv = conversions || PREDEFINED_UOM_CONVERSIONS[fromUOM];
  if (!conv) return quantity;
  
  // If converting to base
  if (toUOM === conv.base) {
    return quantity * getConversionFactor(fromUOM, conv.conversions);
  }
  
  // If converting from base
  if (fromUOM === conv.base) {
    const targetConv = conv.conversions.find(c => c.uom === toUOM);
    if (targetConv) {
      return quantity / targetConv.qty;
    }
  }
  
  return quantity;
}

// Get conversion factor from uom to base
export function getConversionFactor(
  uom: string,
  conversions?: UOMConversion[]
): number {
  const conv = conversions || PREDEFINED_UOM_CONVERSIONS[uom];
  if (!conv) return 1;
  
  // Handle UOMConversions object
  if ('base' in conv) {
    if (uom === conv.base) return 1;
    const found = conv.conversions.find(c => c.uom === uom);
    return found ? found.qty : 1;
  }
  
  // Handle UOMConversion[] array
  if (Array.isArray(conv)) {
    const found = conv.find(c => c.uom === uom);
    return found ? found.qty : 1;
  }
  
  return 1;
}

// Get available conversions for a UOM
export function getConversions(uom: string): UOMConversion[] {
  return PREDEFINED_UOM_CONVERSIONS[uom]?.conversions || [];
}

// Check if a UOM has conversions
export function hasConversions(uom: string): boolean {
  const conv = PREDEFINED_UOM_CONVERSIONS[uom];
  return !!conv && conv.conversions.length > 0;
}

// Get smart display format for quantity
export function getSmartDisplay(
  baseQty: number,
  baseUOM: string,
  decimalDigits: number = 2
): string {
  const conv = PREDEFINED_UOM_CONVERSIONS[baseUOM];
  if (!conv || conv.conversions.length === 0) {
    return `${roundNumber(baseQty, decimalDigits)} ${baseUOM}`;
  }
  
  // Sort conversions by qty descending (biggest first)
  const sortedConvs = [...conv.conversions].sort((a, b) => b.qty - a.qty);
  
  const parts: string[] = [];
  let remaining = baseQty;
  
  for (const c of sortedConvs) {
    if (remaining >= c.qty) {
      const count = Math.floor(remaining / c.qty);
      if (count > 0) {
        parts.push(`${count} ${c.uom}`);
        remaining = remaining % c.qty;
      }
    }
  }
  
  // Add remaining in base unit
  if (remaining > 0.001) {
    parts.push(`${roundNumber(remaining, decimalDigits)} ${baseUOM}`);
  }
  
  return parts.join(' + ') || `0 ${baseUOM}`;
}

// Get suggestions for transfer/request (smart conversion)
export function getTransferSuggestions(
  requestedQty: number,
  baseUOM: string,
  maxSuggestions: number = 3
): { uom: string; qty: number; converted: number; suggestion: string }[] {
  const conv = PREDEFINED_UOM_CONVERSIONS[baseUOM];
  if (!conv) return [];
  
  const suggestions: { uom: string; qty: number; converted: number; suggestion: string }[] = [];
  
  // Add base unit option
  suggestions.push({
    uom: baseUOM,
    qty: requestedQty,
    converted: requestedQty,
    suggestion: `${roundNumber(requestedQty, 0)} ${baseUOM}`,
  });
  
  // Add conversion options
  for (const c of conv.conversions) {
    const converted = requestedQty / c.qty;
    const rounded = Math.round(converted * 100) / 100;
    
    suggestions.push({
      uom: c.uom,
      qty: rounded,
      converted: requestedQty,
      suggestion: `${rounded} ${c.uom}`,
    });
    
    if (suggestions.length >= maxSuggestions) break;
  }
  
  return suggestions;
}

// Round number based on settings
export function roundNumber(
  value: number,
  decimalDigits: number,
  mode: 'up' | 'down' | 'nearest' = 'nearest'
): number {
  const factor = Math.pow(10, decimalDigits);
  
  switch (mode) {
    case 'up':
      return Math.ceil(value * factor) / factor;
    case 'down':
      return Math.floor(value * factor) / factor;
    case 'nearest':
    default:
      return Math.round(value * factor) / factor;
  }
}

// Format currency (IDR)
export function formatCurrencyIDR(value: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// Check if conversion is valid
export function isValidConversion(fromUOM: string, toUOM: string): boolean {
  if (fromUOM === toUOM) return true;
  
  const fromConv = PREDEFINED_UOM_CONVERSIONS[fromUOM];
  if (fromConv && fromConv.base === toUOM) return true;
  
  const toConv = PREDEFINED_UOM_CONVERSIONS[toUOM];
  if (toConv && toConv.base === fromUOM) return true;
  
  return false;
}

// Get base UOM for a given UOM
export function getBaseUOM(uom: string): string {
  return PREDEFINED_UOM_CONVERSIONS[uom]?.base || uom;
}
